import {
  Component,
  EventEmitter,
  Injector,
  Input,
  Output,
  ViewChild,
  ChangeDetectorRef,
  ElementRef,
  Renderer2,
} from '@angular/core';
import { AuthenticationService } from '@portal/shared/auth/authentication';
import { EnvironmentVariableService, PendoService } from '@portal/shared/helpers/src';
import { BaseModalComponent } from '@portal/shared/ui/modal/src';
import { PortalBase } from '../portal-base';
import environment from '../../../../../../../environments/environment';
import { Features } from '../../../../../../../environments/enums/features.enum';
import { IUser, UserRole, UserStatus } from '@portal/entity-services/interfaces';
import cloneDeep from 'lodash-es/cloneDeep';
import get from 'lodash-es/get';
import { TermsAndConditions } from '../enums/terms-and-conditions.enum';
import { ITermsAndConditions } from '@portal/entity-services/interfaces/src/lib/users/interfaces/terms-and-conditions.interface';
import { ToastService } from '@portal/shared/ui/toast';
import { WhiteLabelingService } from '@portal/shared/white-labeling/src';
import { take } from 'rxjs/operators';
import { Subscription, timer } from 'rxjs';
import { MandatoryModalComponent } from '@portal/shared/ui/modal/src/lib/mandatory-modal/mandatory-modal.component';
import { CoockiesService } from '@portal/shared/helpers';

@Component({
  selector: 'vui-portal',
  templateUrl: './portal.component.html',
  styleUrls: ['./portal.component.scss'],
  standalone: false,
})
export class PortalComponent extends PortalBase {
  @Input() withSignInSplash = true; // todo: factor out the splash section into its own component to eliminate this kludge
  @Input() isWhiteLabelingPreview = false;
  @Input() isFromVhq = false;
  @Input() set logoSrc(value: string) {
    if (value) {
      this._logoSrc = value;
    }
  }
  get logoSrc(): string {
    return this._logoSrc;
  }

  @Output() useEvents = new EventEmitter<any>();
  @ViewChild('acceptTermsAndConditionsModal') acceptTnCModal: BaseModalComponent;
  @ViewChild('userActivityModal') userActivityModal: MandatoryModalComponent;
  @ViewChild('timeOutModalContinueButton')
  timeOutModalContinueButton: ElementRef<HTMLButtonElement>;
  @ViewChild('timeOutModalOkButton') timeOutModalOkButton: ElementRef<HTMLButtonElement>;
  termsConditionLink = environment?.TERMS_AND_CONDITIONS?.TC_URL;
  currentTnCVersion = environment?.TERMS_AND_CONDITIONS?.TC_VERSION;
  isFederatedUser: boolean;
  needsToAcceptTnC: boolean;
  needsToAcceptNewVersion: boolean;
  brandText = 'Verifone Central';
  isCBA = EnvironmentVariableService.getVar('CBA');
  isSessionTimeout: boolean;
  isUserIdleTimeout: boolean;
  channel = new BroadcastChannel('activity_channel');
  showNPSSurvey =
    this.featureToggleService.isConfigFeatureActive(Features.NPSSurvey) &&
    !this.whiteLabelingService.isWhiteLabelingEnabled();
  protected readonly AuthenticationService = AuthenticationService;
  private userIdleTimer$: Subscription;
  private userActivity$: Subscription;
  private userActivityModalTimer$: Subscription;
  private userSessionTimer$: Subscription;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private _logoSrc =
    'https://d3slnjjr5hy7v5.cloudfront.net/assets/logos/linear/verifone/verifone_logotype_black.png';

  constructor(
    public injector: Injector,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    private coockiesService: CoockiesService,
    private pendoService: PendoService
  ) {
    super(injector);
    this.whiteLabelingService = injector.get(WhiteLabelingService);
    this.authorizationService.federatedUser$.subscribe((data) => {
      this.isFederatedUser = data;
    });

    this.authenticationService.authEvents.subscribe((event: AuthenticationService.IEvent) => {
      this.useEvents.next({
        type: event.type,
        data: {
          ...event.data,
          authenticatedUser: this.authenticatedUser,
          appsPermissions: this.appsPermissions,
        },
      });

      switch (event.type) {
        case AuthenticationService.Event.LoggedIn: {
          this.setAuthLoadingState(false);
          this.isAuthenticated = true;
          this.brandText = this.whiteLabelingService.getBrandText() || this.brandText;
          this.pendoService.initialize(this.authenticatedUser);
          if (!this.isWebView && !this.showUnauthenticatedOutlet) {
            this.showAcceptTnCModal();
            this.loadNPSSurveyOnLogin();
          }
          break;
        }
        case AuthenticationService.Event.LoggedOut:
          this.isAuthenticated = false;
          this.setAuthLoadingState(false);
          this.subscription.unsubscribe();
          if (
            !EnvironmentVariableService.getVar('CBA') &&
            !!!EnvironmentVariableService.getVar('IONIC.ON') &&
            !this.isFederatedUser
          ) {
            this.authenticationService.authenticate(localStorage.getItem('redirect_path'), AuthenticationService.Event.LoggedOut, this.isWebView);
          }
          break;
        case AuthenticationService.Event.InProgress:
          this.setAuthLoadingState(true);
          break;
        case AuthenticationService.Event.AuthResponseSuccess:
        default:
          break;
      }
    });

    this.initSessionTimers();
    this.channel.onmessage = (message) => {
      if (message.data === 'reset_timer') {
        this.resetUserActivity();
      } else if (message.data === 'remove_silent_refresh') {
        this.removeSilentRefresh();
      }
    };
  }

  loadNPSSurveyOnLogin(): void {
    if (this.showNPSSurvey && !localStorage.getItem('survey_shown')) {
      this.loadSurveyPopup(false);
    }
  }

  loadSurveyPopup(deleteCookie: boolean): void {
    if (this.showNPSSurvey && !document.querySelector('.smcx-widget.smcx-modal:not(.smcx-hide)')) {
      if (deleteCookie) {
        this.coockiesService.deleteMatchingCookies(/^smcx_.*_last_shown_at$/);
      }

      document
        .querySelectorAll(
          '.smcx-modal, .smcx-widget, #smcx-sdk , #__smcx__, iframe[src*="surveymonkey.com"]',
        )
        .forEach((el) => el.remove());

      const script = this.renderer.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.id = 'smcx-sdk';
      script.src = environment?.SURVEY_SCRIPTS?.NPS_SURVEY;

      document.body.appendChild(script);

      script.onload = function () {
        setTimeout(() => {
          const el = document.querySelector('.smcx-widget .smcx-modal');
          if (el) {
            el.setAttribute('tabindex', '0');
          }

          const modalClose = document.querySelector('.smcx-modal-close');
          if (modalClose instanceof HTMLElement) {
            modalClose.setAttribute('tabindex', '0');
            modalClose.setAttribute('role', 'button');
            modalClose.setAttribute('aria-label', 'Close survey modal');

            modalClose.addEventListener('keydown', (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                modalClose.click();
              }
            });
          }
        }, 2000);
      };
      localStorage.setItem('survey_shown', 'true');
    }
  }

  continueUserSession(): void {
    this.userActivityModal.close();
    this.initUserActivity();
  }

  closeUserSession(): void {
    this.userActivityModal?.close();
    this.userActivity$?.unsubscribe();
    this.userSessionTimer$?.unsubscribe();
    this.removeUserActivityModalTimer();
    this.removeUserIdleTimer();
    this.removeSilentRefresh();
    this.channel.postMessage('remove_silent_refresh');
    this.authenticationService.logout({ skipLocationChange: true });
    if (EnvironmentVariableService.getVar('CBA') || EnvironmentVariableService.getVar('IONIC.ON')) {
      window.location.assign(`${window.location.origin}/`);
    }
  }

  initUserActivity(): void {
    this.userActivity$ = this.userActivityService.getActivityEvents().subscribe(() => {
      this.resetUserActivity();
      this.channel.postMessage('reset_timer');
    });
    this.resetUserActivity();
    this.channel.postMessage('reset_timer');
  }

  initSessionTimers(): void {
    this.authenticationService.accessToken$.subscribe(() => {
      if (!this.userActivity$) {
        this.initUserActivity();
      }
      if (!this.userSessionTimer$ && localStorage.getItem('session_start_time')) {
        this.startSessionTimer();
      }
    });
    if (localStorage.getItem('access_token')) {
      if (!this.userActivity$) {
        this.initUserActivity();
      }
      if (!this.userSessionTimer$ && localStorage.getItem('session_start_time')) {
        this.startSessionTimer();
      }
    }
    this.authenticationService.sessionTimer.subscribe(() => this.startSessionTimer());
  }

  resetUserActivity(): void {
    this.removeUserIdleTimer();
    this.removeUserActivityModalTimer();
    this.startUserIdleTimer();
  }

  startUserIdleTimer(): void {
    this.userIdleTimer$ = timer(
      this.calculateUserInactivityTimeout(
        this.authenticationService.authConfigs.userInactivityTimeout,
        this.authenticationService.authConfigs.userInactivityTimeoutOffset,
      ),
    ).subscribe(() => {
      this.isUserIdleTimeout = true;
      this.isSessionTimeout = false;
      this.removeUserIdleTimer();
      this.userActivity$.unsubscribe();
      this.showTimeoutModal();
      this.cdr.detectChanges();
      this.userActivityModalTimer$ = timer(
        this.authenticationService.authConfigs.userInactivityTimeoutOffset,
      ).subscribe(() => {
        this.closeUserSession();
      });
    });
  }

  showTimeoutModal(): void {
    this.userActivityModal.open();
    setTimeout(() => {
      if (this.timeOutModalContinueButton) {
        this.timeOutModalContinueButton.nativeElement.focus();
      } else if (this.timeOutModalOkButton) {
        this.timeOutModalOkButton.nativeElement.focus();
      }
    }, 0);
  }

  startSessionTimer(): void {
    const currentTime = new Date().getTime();
    const sessionStartTime = parseInt(localStorage.getItem('session_start_time'), 10);
    const diff =
      this.authenticationService.authConfigs.userSessionTimeout - (currentTime - sessionStartTime);
    if (diff < 0) {
      this.closeUserSession();
      return;
    }
    this.userSessionTimer$ = timer(
      this.calculateUserInactivityTimeout(
        diff,
        this.authenticationService.authConfigs.userInactivityTimeoutOffset,
      ),
    ).subscribe(() => {
      this.authenticationService.sessionTimeoutModalTriggered = true;
      this.isSessionTimeout = true;
      this.isUserIdleTimeout = false;
      this.removeUserIdleTimer();
      this.userActivity$.unsubscribe();
      this.userActivityModal.open();
      timer(this.authenticationService.authConfigs.userInactivityTimeoutOffset).subscribe(() => {
        this.closeUserSession();
      });
    });
  }

  removeUserIdleTimer(): void {
    this.userIdleTimer$?.unsubscribe();
  }

  removeUserActivityModalTimer(): void {
    this.userActivityModalTimer$?.unsubscribe();
  }

  removeSilentRefresh(): void {
    this.authenticationService.removeSilentRefreshEventListener();
    this.authenticationService.removeTokenRefreshTimers();
  }

  calculateUserInactivityTimeout(timeout: number = 15, offset: number = 2): number {
    return timeout - offset;
  }

  updateUserTermConditions(termsAndConditions: ITermsAndConditions): void {
    const userDetails = this.getUserDetailsToSave();
    const userDetailsToSave = cloneDeep(userDetails);
    userDetailsToSave.termsAndConditions = termsAndConditions;

    this.esUserService.partialUpdate(userDetailsToSave).subscribe(
      (updatedUser) => {
        this.toastService.showToast(
          $localize`The consent of ${updatedUser.name} on Terms and Conditions is registered`,
        );
      },
      (errorObject: Object) => {
        this.notifierService.error(
          $localize`Error` +
            ` (${get(errorObject, 'error.status')}) ${get(errorObject, 'error.error.message')}`,
        );
      },
    );
  }

  getTodayDate(): Date {
    const currentDate: Date = new Date();
    return cloneDeep(currentDate);
  }

  get isTermsAndConditionsEnabled(): boolean {
    return this.featureToggleService.isFeatureActive(Features.TermsAndConditions);
  }

  getUserDetailsToSave(): IUser {
    return {
      userUid: this.userService.userDetails.userUid,
      name: this.userService.userDetails.name,
      nameDetails: this.userService.userDetails.nameDetails,
      email: this.userService.userDetails.email,
      roles: this.userService.userDetails.roles as UserRole[],
      status: this.userService.userDetails.status as UserStatus,
      language: this.userService.userDetails.language,
    };
  }

  showAcceptTnCModal(): void {
    this.needsToAcceptTnC = false;
    this.needsToAcceptNewVersion = false;
    this.whiteLabelingService
      .isWhiteLabelingExists()
      .pipe(take(1))
      .subscribe((isWhitelabeledOrg) => {
        if (!isWhitelabeledOrg && this.isTermsAndConditionsEnabled) {
          if (
            !this.userService.userDetails?.termsAndConditions ||
            this.userService.userDetails?.termsAndConditions?.version !== this.currentTnCVersion ||
            this.userService.userDetails?.termsAndConditions?.status !== TermsAndConditions.Accepted
          ) {
            // Needs to accept the terms and conditions
            if (
              !this.userService.userDetails?.termsAndConditions ||
              this.userService.userDetails?.termsAndConditions?.status !==
                TermsAndConditions.Accepted
            ) {
              this.needsToAcceptTnC = true;
            }

            // Needs to accept just the new version
            else if (
              this.userService.userDetails?.termsAndConditions?.version !== this.currentTnCVersion
            ) {
              this.needsToAcceptNewVersion = true;
            }

            if (this.needsToAcceptNewVersion || this.needsToAcceptTnC) {
              this.acceptTnCModal?.open();
            }
          }
        }
      });
  }

  onAcceptTnC(): void {
    this.acceptTnCModal.close();
    const termsAndConditions: ITermsAndConditions = {
      status: TermsAndConditions.Accepted,
      owner: TermsAndConditions.Verifone,
      version: this.currentTnCVersion,
      userActionDate: this.getTodayDate().toISOString(),
    };

    this.updateUserTermConditions(termsAndConditions);
  }

  onTnCLogout(): void {
    const termsAndConditions: ITermsAndConditions = {
      status: TermsAndConditions.Rejected,
      owner: TermsAndConditions.Verifone,
      version: '',
      userActionDate: this.getTodayDate().toISOString(),
    };

    this.updateUserTermConditions(termsAndConditions);
    this.authenticationService.logout();
  }
}
